import React, { useState, useRef } from 'react';
import { Upload, X, FileText, Download } from 'lucide-react';
import api from '../services/api';

interface PDFUploaderProps {
  onPDFSelect: (pdfUrl: string, originalName: string) => void;
  currentPDF?: string;
  currentName?: string;
}

const PDFUploader: React.FC<PDFUploaderProps> = ({ 
  onPDFSelect, 
  currentPDF, 
  currentName 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [pdfId, setPdfId] = useState(currentPDF || '');
  const [fileName, setFileName] = useState(currentName || '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://web-production-83f53.up.railway.app';

  const handleFileSelect = async (file: File) => {
    if (file.type !== 'application/pdf') {
      alert('Veuillez sélectionner un fichier PDF valide');
      return;
    }

    setIsUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      
      const response = await api.post('/api/upload-pdf/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;
      
      setPdfId(result.pdf_id);
      setFileName(result.file_name);
      onPDFSelect(result.pdf_id, result.file_name);
      setIsUploading(false);
      
    } catch (error: any) {
      console.error('Erreur lors de l\'upload:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erreur inconnue';
      alert(`Erreur lors de l'upload du PDF: ${errorMessage}`);
      setIsUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleRemove = () => {
    setPdfId('');
    setFileName('');
    onPDFSelect('', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {!pdfId ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-red-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileInputChange}
            className="hidden"
          />
          
          {isUploading ? (
            <div className="space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mx-auto"></div>
              <p className="text-sm text-gray-600">Upload en cours...</p>
            </div>
          ) : (
            <div className="space-y-2">
              <FileText className="w-12 h-12 text-gray-400 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Cliquez pour sélectionner un PDF
                </p>
                <p className="text-xs text-gray-500">
                  ou glissez-déposez un fichier PDF ici
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Format PDF uniquement
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg p-4 bg-red-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <FileText className="w-8 h-8 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {fileName || 'Document PDF'}
                </p>
                <div className="flex items-center space-x-2 mt-1">
                  <a
                    href={`${API_BASE_URL}/api/pdf/${pdfId}/download/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-red-600 hover:text-red-800 flex items-center space-x-1"
                  >
                    <Download className="w-3 h-3" />
                    <span>Télécharger</span>
                  </a>
                </div>
              </div>
            </div>
            <button
              onClick={handleRemove}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Supprimer le PDF"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFUploader;
